/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { useIntl, defineMessages } from 'react-intl-next';
import type { ReactNode } from 'react';

import { Box, Stack, Text, Inline } from '@atlaskit/primitives/compiled';
import Heading from '@atlaskit/heading';
import { cssMap, jsx } from '@atlaskit/css';
import { token, useThemeObserver } from '@atlaskit/tokens';
import Button, { IconButton } from '@atlaskit/button/new';
import CrossIcon from '@atlaskit/icon/core/cross';

import { VideoButton } from './VideoButton.tsx';
const styles = cssMap({
	banner: {
		borderColor: token('color.border'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderRadius: token('radius.small'),
		backgroundColor: token('elevation.surface.sunken'),
		overflow: 'hidden',
	},
	text: {
		minWidth: '300px',
		maxWidth: '400px',
	},
	closeWrapper: {
		alignSelf: 'flex-start',
	},
	blue: {
		color: token('color.text.brand'),
		whiteSpace: 'nowrap',
	},
	underline: {
		textDecoration: 'none',
		paddingBottom: token('space.250'),
		backgroundRepeat: 'no-repeat',
		backgroundPositionY: '60%',
		backgroundSize: 'contain',
	},
	content: {
		flexGrow: 1,
		flexWrap: 'wrap',
		paddingBlock: token('space.200'),
	},
	spacer: { flexGrow: 1, justifyContent: 'flex-end' },
});

const Underlined = ({ children }: { children: React.ReactNode }) => {
	const { colorMode } = useThemeObserver(); // Ensure the theme is observed for styling

	return (
		<Box
			xcss={styles.underline}
			as="u"
			style={{
				backgroundImage: `url(${colorMode === 'dark' ? require('../../assets/squiggle-dark.svg') : require('../../assets/squiggle-light.svg')})`,
			}}
		>
			{children}
		</Box>
	);
};
export const Banner = ({ onClick, onClose }: { onClick: () => void; onClose: () => void }) => {
	const { formatMessage } = useIntl();

	return (
		<Inline
			alignInline="center"
			alignBlock="center"
			space="space.100"
			spread="space-between"
			xcss={styles.banner}
		>
			<VideoButton onClick={onClick} />
			<Inline
				xcss={styles.content}
				alignBlock="center"
				space="space.100"
				rowSpace="space.150"
				spread="space-between"
			>
				<Inline alignBlock="center" space="space.100">
					<Stack xcss={styles.text} space="space.050">
						<Heading size="large">
							{formatMessage(messages.heading, {
								u: (parts: ReactNode[]) => <Underlined>{parts}</Underlined>,
								b: (parts: ReactNode[]) => parts, // backwards compatibility for old translations
								em: (parts: ReactNode[]) => parts, // backwards compatibility for old translations
							})}
						</Heading>
						<Text>{formatMessage(messages.body)}</Text>
					</Stack>
				</Inline>

				<Inline xcss={styles.spacer} space="space.100">
					<Button appearance="primary" onClick={onClick}>
						{formatMessage(messages.playNow)}
					</Button>
				</Inline>
			</Inline>
			<Box xcss={styles.closeWrapper}>
				<IconButton
					appearance="subtle"
					label={formatMessage(messages.dismissCta)}
					icon={CrossIcon}
					onClick={onClose}
				/>
			</Box>
		</Inline>
	);
};

const messages = defineMessages({
	heading: {
		id: 'modernize-confluence-ipm-modal.heading',
		defaultMessage: 'Designed for <u>Collaboration</u>',
		description: 'This is the heading in a modal that announces new Confluence features',
	},
	body: {
		id: 'modernize-confluence-ipm-modal.body',
		defaultMessage:
			'We’ve been adding capabilities to make Confluence the best tool for teams to brainstorm and bring ideas to life.',
		description: 'This is the body text in a modal that announces new Confluence features',
	},
	dismissCta: {
		id: 'modernize-confluence-ipm-modal.dismissCta',
		defaultMessage: 'Dismiss',
		description: 'This text is shown on a button that closes the modal',
	},
	playNow: {
		id: 'modernize-confluence-ipm-modal.playNow',
		defaultMessage: 'Show me',
		description: 'This text is shown on a button that plays the video',
	},
});
